import './index.scss'
import React, { useEffect, useMemo } from 'react'
import WarningSvg from './images/warning.svg?react'
import { useAppSelector } from '../../../app/hooks'
import {
  POINTS_STATUS_CONFIG_KEY,
  parsePointsStatusCopy,
  POINTS_THEMES,
  POINTS_STATE_STYLES,
  vipKindToThemeKey,
  computeUsageState,
  formatWan,
  DEFAULT_PER_QUERY_COST,
  ALERT_HINT_BG,
  ALERT_HINT_COLOR,
  ALERT_ACTION_COLOR
} from './config'
import type { PointsStatusCardProps, PointsCardContext } from './types'

/**
 * 「我的积分」状态卡片（通用公共组件）。
 *
 * 4 身份 × 4 使用状态 = 16 种展示，全部由数据驱动：
 *   - vipKind（对齐 CONFIG.VIP_KIND）→ 主题色 + 文案模板 + CTA
 *   - used / total → 组件内部算出 usageState（满额 / 一般 / 预警 / 不足 1 次）
 *     → 决定警示色（危急红 > 预警橙 > 身份主色）+ 提示图标 + 底部 chip
 *
 * 文案走 KV（POINTS_STATUS_CONFIG_KEY），运营可改；颜色按设计稿。
 * 数值大字展示「剩余积分」(total − used)，满额态即等于 total。
 *
 * 用法：
 *   <PointsStatusCard vipKind={vipKind} total={3000000} used={1000000}
 *     onUpgrade={...} onContact={...} />
 */
const PointsStatusCard: React.FC<PointsStatusCardProps> = ({
  vipKind,
  total,
  used,
  perQueryCost = DEFAULT_PER_QUERY_COST,
  onCtaClick,
  onContactClick,
  onExpose,
  className
}) => {
  const rawCopy = useAppSelector(
    (state: any) => state.main.config?.[POINTS_STATUS_CONFIG_KEY]
  )
  const copy = useMemo(() => parsePointsStatusCopy(rawCopy), [rawCopy])

  // ─── 归一化数据 ───
  const safeTotal = Math.max(0, total || 0)
  const safeUsed = Math.min(Math.max(0, used || 0), safeTotal)
  const remaining = Math.max(0, safeTotal - safeUsed)

  const themeKey = vipKindToThemeKey(vipKind)
  const theme = POINTS_THEMES[themeKey]
  const kindCopy = copy.kinds[themeKey]

  const usageState = computeUsageState(safeTotal, safeUsed, perQueryCost)
  const stateCopy = copy.states[usageState]
  const isAlert = usageState === 'warning' || usageState === 'critical'

  // ─── 配色：危急红 > 预警橙 > 身份主色 ───
  const stateStyle = isAlert ? POINTS_STATE_STYLES[usageState] : null
  const accent = stateStyle ? stateStyle.accent : theme.main // 数值 / 进度 / chip
  const softBg = stateStyle ? stateStyle.soft : theme.soft // 积分区 / chip 底
  const pbTrack = stateStyle ? stateStyle.pbTrack : '#ffffff' // 进度条轨道
  // 提示区 / CTA / 客服固定橙色系(p3 设计:与积分区的危急红区分,避免整片红太刺眼;
  // 数值 / 进度仍用 accent 跟随状态红, 只有横幅 + 按钮 + 客服走固定橙)
  const hintBg = isAlert ? ALERT_HINT_BG : theme.soft
  const hintColor = isAlert ? ALERT_HINT_COLOR : theme.deep
  const actionColor = isAlert ? ALERT_ACTION_COLOR : theme.main

  // ─── 进度（已用占比，留一点最小可见宽度）───
  const percent = safeTotal ? Math.min(100, (safeUsed / safeTotal) * 100) : 0
  const pfWidth = `${Math.max(percent, 0.8)}%`

  // ─── 文案拼接 ───
  // 数值说明：「（本月）已用 X / 总额度 Y（· 不足 1 次）」
  const usedPrefix = kindCopy.monthly ? '本月已用' : '已用'
  const usageDetail =
    `${usedPrefix} ${formatWan(safeUsed)} / 总额度 ${formatWan(safeTotal)}` +
    (usageState === 'critical' ? ' · 不足 1 次' : '')

  // 提示横幅：常规 vs 预警 / 危急
  const hintText = isAlert ? kindCopy.alertHint : kindCopy.normalHint
  const hintEmoji = themeKey === 'none' ? '🎁' : '💎'

  // 回调上下文：身份 + 状态 + 数值，随事件透传给调用方（埋点 / 区分行为）
  const action: 'upgrade' | 'addon' = themeKey === 'super' ? 'addon' : 'upgrade'
  const ctx: PointsCardContext = {
    vipKind,
    themeKey,
    usageState,
    action,
    total: safeTotal,
    used: safeUsed,
    remaining
  }

  // 曝光埋点：组件首次渲染触发一次
  useEffect(() => {
    if (onExpose) onExpose(ctx)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className={`points-status-card${className ? ` ${className}` : ''}`}>
      <div className="psc-card">
        {/* header */}
        <div className="psc-header">
          <span className="psc-header-icon">💎</span>
          <span className="psc-header-title">我的积分</span>
          <span
            className="psc-tag"
            style={{ background: theme.tagBg, color: theme.tagColor }}
          >
            {kindCopy.tag}
          </span>
        </div>

        {/* 积分区 */}
        <div className="psc-section" style={{ background: softBg }}>
          <div className="psc-section-label">
            {/* 仅非会员（试用积分）带「· 状态后缀」；会员固定「本月可用积分」无后缀 */}
            {themeKey === 'none'
              ? `${kindCopy.sectionMain} · ${stateCopy.sectionSub}`
              : kindCopy.sectionMain}
          </div>
          <div className="psc-amount" style={{ color: accent }}>
            {formatWan(remaining)}
          </div>
          <div className="psc-progress" style={{ background: pbTrack }}>
            <div
              className="psc-progress-fill"
              style={{ width: pfWidth, background: accent }}
            />
          </div>
          <div className="psc-usage">{usageDetail}</div>
        </div>

        {/* 提示横幅 */}
        <div className="psc-hint" style={{ background: hintBg }}>
          {isAlert ? (
            <WarningSvg className="psc-hint-icon-svg" aria-hidden="true" />
          ) : (
            <span className="psc-hint-icon">{hintEmoji}</span>
          )}
          <span className="psc-hint-text" style={{ color: hintColor }}>
            {hintText}
          </span>
        </div>

        {/* CTA */}
        <button
          type="button"
          className="psc-cta"
          style={{ background: actionColor }}
          onClick={() => onCtaClick?.(ctx)}
        >
          {kindCopy.cta}
        </button>

        {/* 联系客服 */}
        <div
          className="psc-contact"
          style={{ color: actionColor }}
          onClick={() => onContactClick?.(ctx)}
        >
          联系客服
        </div>
      </div>
    </div>
  )
}

export default PointsStatusCard
